import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { api, setLogoutHandler } from '../api/client';

// Attach (or clear) the current user on Sentry events so errors are tied to a user.
function setSentryUser(user) {
  if (user) {
    Sentry.setUser({ id: user.id, username: user.username, is_admin: user.is_admin });
  } else {
    Sentry.setUser(null);
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Internal logout function that just clears state (used for 401 errors)
  const clearAuth = useCallback(() => {
    localStorage.removeItem('access_token');
    setUser(null);
  }, []);

  // Full logout function that calls API and clears state
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore network/API errors during logout; always clear local session.
    }
    clearAuth();
  }, [clearAuth]);

  // Keep Sentry's user context in sync with auth state.
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  // Set up logout handler for API client to call on 401 errors
  // This uses clearAuth instead of logout to avoid calling the API with an expired token
  useEffect(() => {
    setLogoutHandler(() => {
      clearAuth();
    });
  }, [clearAuth]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.me()
      .then(setUser)
      .catch(() => {
        localStorage.removeItem('access_token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    setError('');
    const data = await api.login({ email, password });
    localStorage.setItem('access_token', data.access_token);
    const me = await api.me();
    setUser(me);
    return me;
  };

  const signup = async (payload) => {
    setError('');
    await api.signup(payload);
    return login(payload.email, payload.password);
  };

  const value = useMemo(() => ({ user, loading, error, login, signup, logout, setError, setUser }), [user, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
